import { useLogout } from '@blattnercompany/react-auth'
import { LogOut, Settings } from 'lucide-react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { clearUserStoredState } from '@/hooks/useStoredState'

import './UserChip.css'

export type CurrentUser = {
  name: string
  initials: string
  role?: string
  email?: string
}

type Props = {
  user: CurrentUser
}

/**
 * Signed-in user menu in the top bar, on the shadcn Avatar + DropdownMenu
 * (avatar image support arrives free once Entra profile photos exist). The
 * user comes from Entra ID claims, read off MSAL by useCurrentUser.
 */
function UserChip({ user }: Props) {
  // Redirect sign-out from @blattnercompany/react-auth; lands back at
  // msalConfig's postLogoutRedirectUri, which sends the next visit through SSO.
  // beforeLogout runs first and clears only stagehand.* keys (never MSAL's
  // cache); it swallows storage errors, so it can't block sign-out.
  const logout = useLogout({ beforeLogout: clearUserStoredState })
  const detail = user.role || user.email

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="userchip" aria-label="User menu">
        <Avatar className="userchip__avatar">
          <AvatarFallback className="userchip__avatar-fallback">
            {user.initials}
          </AvatarFallback>
        </Avatar>
        <span className="userchip__text">
          {user.name}
          {detail && <span className="userchip__role">{detail}</span>}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          {user.name}
          {detail && <span className="userchip__menu-role">{detail}</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* TODO: wire to a settings surface once one exists — the item is
            scaffolding for now and just closes the menu. min-h-11 lifts the
            vendored py-1 (~28px) to the 44px touch minimum. */}
        <DropdownMenuItem className="min-h-11">
          <Settings aria-hidden="true" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem className="min-h-11" onSelect={() => void logout()}>
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default UserChip
